import React, { useState, useEffect } from 'react';
import MultiSelectDropdownInline from '../multi-select-inline';
import { LocalTradeSelection, MechanicalContractorFormErrors } from './dialogTypes';
import { Local, Trade, lookupService } from '../../services/lookups.service';

interface LocalTradeSelectionProps {
  localTradeSelections: LocalTradeSelection[];
  onLocalTradeSelectionsChange: (selections: LocalTradeSelection[]) => void;
  errors: MechanicalContractorFormErrors;
}


const LocalTradeSelectionComponent: React.FC<LocalTradeSelectionProps> = ({
  localTradeSelections,
  onLocalTradeSelectionsChange,
  errors
}) => {
  const [locals, setLocals] = useState<Local[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loadingLocals, setLoadingLocals] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(false);

  useEffect(() => {
    loadLocals();
    loadTrades();
  }, []);

  const loadLocals = async () => {
    setLoadingLocals(true);
    try {
      const localsData = await lookupService.getLocals();
      setLocals(localsData);
    } catch (error) {
      console.error('Error loading locals:', error);
    } finally {
      setLoadingLocals(false);
    }
  };

  const loadTrades = async () => {
    setLoadingTrades(true);
    try {
      const tradesData = await lookupService.getTrades();
      // Filter to only show UA trades
      const uaTrades = tradesData.filter(t => t.category === 'UA');
      setTrades(uaTrades);
    } catch (error) {
      console.error('Error loading trades:', error);
    } finally {
      setLoadingTrades(false);
    }
  };


  const updateLocalSelection = (index: number, field: keyof LocalTradeSelection, value: any) => {
    const newSelections = [...localTradeSelections];
    newSelections[index] = {
      ...newSelections[index],
      [field]: value
    };
    onLocalTradeSelectionsChange(newSelections);
  };

  const tradeOptions = trades.map(trade => ({
    id: trade.id,
    name: trade.name,
    category: trade.category
  }));

  const selectedLocalIds = localTradeSelections.map(selection => selection.localUnionId);

  const handleLocalUnionsChange = (selectedIds: string[]) => {
    // Create new selections for newly selected locals
    const newSelections = selectedIds.map(localId => {
      // Check if this local already exists in selections
      const existingSelection = localTradeSelections.find(selection => selection.localUnionId === localId);
      if (existingSelection) {
        return existingSelection;
      }

      // Create new selection for this local
      const selectedLocal = locals.find(local => local.id === localId);
      return {
        localUnionId: localId,
        localUnionName: selectedLocal ? `${selectedLocal.code} - Local ${selectedLocal.unionNumber}` : '',
        tradeIds: [],
        unionType: 'union' as const
      };
    });

    onLocalTradeSelectionsChange(newSelections);
  };

  return (
    <div className="pb-4">
      <div className="mb-3">
        <label className="block text-small font-medium text-text-secondary mb-2">
          Local Unions (Optional)
        </label>
        <MultiSelectDropdownInline
          options={locals}
          selectedValues={selectedLocalIds}
          onSelect={handleLocalUnionsChange}
          getValue={(local: Local) => local.id}
          getDisplayText={(local: Local) => `${local.code} - Local ${local.unionNumber}`}
          getGroup={(local: Local) => local.province?.name || 'Other'}
          placeholder="Select local unions"
          testId="local-unions-multiselect"
        />
      </div>

      {/* Trades Selection for Selected Locals */}
      {localTradeSelections.length > 0 && (
        <div className="mt-4">
          <label className="block text-small font-medium text-text-secondary mb-2">
            Trades for Selected Local Unions (Optional)
          </label>
          <div className="space-y-3">
            {localTradeSelections.map((selection, index) => (
              <div key={selection.localUnionId} className="border border-gray-200 rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    {selection.localUnionName}
                  </h4>
                  {selection.tradeIds.length > 0 && (
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                      {selection.tradeIds.length} trade{selection.tradeIds.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <MultiSelectDropdownInline
                  options={tradeOptions}
                  selectedValues={selection.tradeIds}
                  onSelect={(selectedIds: string[]) => updateLocalSelection(index, 'tradeIds', selectedIds)}
                  getValue={(trade) => trade.id}
                  getDisplayText={(trade) => trade.name}
                  placeholder="Select trades for this local union"
                  testId={`trades-selection-${index}`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Select which trades this contractor provides for {selection.localUnionName}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {errors.localTradeSelections && (
        <p className="mt-1 text-xs text-red-500">{errors.localTradeSelections}</p>
      )}
    </div>
  );
};

export default LocalTradeSelectionComponent;